import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, CheckCircle2, Clock, Building2, KeyRound, ArrowUpRight,
  PlayCircle, TrendingUp,
} from "lucide-react";

export default function Dashboard() {
  const { user, refresh, loading } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [showSchool, setShowSchool] = useState(false);
  const [schoolName, setSchoolName] = useState("");
  const [joinCode, setJoinCode] = useState("");

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: lsn }, { data: prog }, { data: chars }] = await Promise.all([
          api.get("/lessons"),
          api.get("/progress"),
          api.get("/characters"),
        ]);
        setLessons(lsn || []);
        setProgress(prog || []);
        setCharacters(chars || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user]);

  const createSchool = async () => {
    if (!schoolName.trim()) return;
    try {
      await api.post("/schools", { name: schoolName });
      toast.success("School created");
      setShowSchool(false);
      await refresh();
    } catch (e) {
      toast.error("Could not create school");
    }
  };

  const joinSchoolFn = async () => {
    if (!joinCode.trim()) return;
    try {
      const { data } = await api.post("/schools/join", { code: joinCode });
      if (data?.status === "pending") {
        toast.success(`Request sent to ${data.school_name}. Awaiting admin approval.`);
      } else if (data?.status === "already_member") {
        toast.info("You are already in this school.");
      } else {
        toast.success("Joined school");
      }
      setShowSchool(false);
      await refresh();
    } catch (e) {
      toast.error("Invalid code");
    }
  };

  const cancelPending = async () => {
    try {
      await api.post("/schools/leave");
      toast.success("Request cancelled");
      await refresh();
    } catch (e) { toast.error("Failed"); }
  };

  if (!user) return null;
  const progressByLesson = Object.fromEntries(progress.map((p) => [p.lesson_id, p]));
  const completedCount = progress.filter((p) => p.completed_at).length;
  const scoredProgress = progress.filter((p) => p.score != null);
  const avgScore = scoredProgress.length
    ? Math.round(scoredProgress.reduce((a, p) => a + p.score, 0) / scoredProgress.length)
    : null;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="dashboard-page">
      {/* HEADER */}
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">
            {user.email}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">
            Welcome back, {user.name?.split(" ")[0]}
          </h1>
          {user.school ? (
            <div className="mt-3 inline-flex items-center gap-2 text-slate-600">
              <Building2 className="w-4 h-4 text-[var(--brand-primary)]" />
              <span className="text-sm font-medium">{user.school.name}</span>
              <span className="status-badge status-brand">{user.school.plan?.replace("_", " ")}</span>
            </div>
          ) : user.pending_school_id ? (
            <div className="mt-4 instrument-panel p-4 inline-block" data-testid="pending-state">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-accent)]">Awaiting approval</div>
              <div className="text-sm text-slate-600 mt-1">Your request to join is pending the school admin.</div>
              <button onClick={cancelPending} className="text-[11px] uppercase tracking-wider font-semibold text-red-600 hover:text-red-700 mt-3" data-testid="cancel-pending-btn">
                Cancel request
              </button>
            </div>
          ) : user.role === "super_admin" ? (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[var(--brand-light)] border border-[var(--brand-primary)]/20" data-testid="super-admin-status">
              <Building2 className="w-4 h-4 text-[var(--brand-primary)]" />
              <span className="text-sm font-medium text-[var(--brand-primary)]">All schools · Platform overview</span>
              <span className="status-badge status-brand">Super admin</span>
            </div>
          ) : (
            <div className="mt-3 text-slate-600 text-sm">
              You aren&apos;t part of a school yet.
              <button
                className="ml-2 text-[var(--brand-primary)] hover:underline font-medium"
                onClick={() => setShowSchool(true)}
                data-testid="setup-school-btn"
              >
                Set up
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Modules" value={lessons.length} icon={<BookOpen className="w-4 h-4" />} />
          <Kpi label="Completed" value={completedCount} icon={<CheckCircle2 className="w-4 h-4" />} />
          <Kpi label="Avg score" value={avgScore != null ? `${avgScore}%` : "—"} icon={<TrendingUp className="w-4 h-4" />} />
        </div>
      </div>

      {/* LESSONS */}
      <section className="mt-12">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Training library</div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1">Available modules</h2>
          </div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {lessons.map((l) => {
            const p = progressByLesson[l.id];
            const launchLesson = () => {
              const route = l.interactive ? `/lesson-interactive/${l.id}` : `/lesson/${l.id}`;
              const w = 1480, h = 900;
              const left = Math.max(0, (window.screen.width - w) / 2);
              const top  = Math.max(0, (window.screen.height - h) / 2);
              const popup = window.open(
                route,
                `lesson_${l.id}`,
                `popup=yes,width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
              );
              if (!popup || popup.closed) window.location.href = route;
            };
            return (
              <div
                key={l.id}
                onClick={launchLesson}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") launchLesson(); }}
                className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[var(--brand-primary)]/40 hover:shadow-md transition-all cursor-pointer"
                data-testid={`lesson-card-${l.id}`}
              >
                <div className="relative aspect-video bg-slate-100 overflow-hidden">
                  {l.cover_image && (
                    <img src={fileUrl(l.cover_image)} alt="" className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-white bg-black/40 backdrop-blur px-2 py-1 rounded-sm" data-testid={`module-chip-${l.id}`}>
                      {l.module}
                    </span>
                    {l.interactive && (
                      <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-900 bg-[var(--brand-accent)] px-2 py-1 rounded-sm">
                        Interactive
                      </span>
                    )}
                    <PlayCircle className="w-8 h-8 text-white drop-shadow-lg opacity-0 group-hover:opacity-100 transition" />
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {!p && (
                      <span className="status-badge status-neutral" data-testid={`status-not-started-${l.id}`}>Not started</span>
                    )}
                    {p && !p.completed_at && (
                      <span className="status-badge status-warning" data-testid={`status-in-progress-${l.id}`}>In progress</span>
                    )}
                    {p?.completed_at && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); window.location.href = `/report/${l.id}`; }}
                          className="status-badge status-info hover:opacity-80"
                          data-testid={`view-report-${l.id}`}
                        >
                          View report <ArrowUpRight className="w-3 h-3" />
                        </button>
                        <span className="status-badge status-success">
                          <CheckCircle2 className="w-3 h-3" /> {p.score}%
                        </span>
                      </>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 tracking-tight">{l.title}</h3>
                  <p className="text-slate-600 text-sm mt-1.5 line-clamp-2 leading-relaxed">{l.subtitle}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {l.duration_minutes} min</div>
                    <div className="text-[var(--brand-primary)] font-semibold text-[11px] uppercase tracking-wider">Launch →</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* INSTRUCTORS */}
      {characters.length > 0 && (
        <section className="mt-16">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Your instructors</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1">Flight cadre</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5">
            {characters.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`character-card-${c.id}`}>
                <div className="aspect-square overflow-hidden bg-slate-100">
                  <img src={c.card_image_url || c.image_url} alt={c.callsign} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 border-t border-slate-100">
                  <div className="font-heading font-semibold text-slate-900 tracking-tight">{c.callsign}</div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-1">{c.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SCHOOL DIALOG */}
      <Dialog open={showSchool} onOpenChange={setShowSchool}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 rounded-sm" data-testid="school-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Join a flying school</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-primary)] mb-2">Join with school code</div>
              <p className="text-slate-600 text-sm mb-3">Enter the code your flight school gave you. Your access request will be reviewed by a school admin.</p>
              <div className="flex gap-2">
                <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. BUZZTOWER" className="bg-white border-slate-300 rounded-sm font-mono uppercase" data-testid="join-code-input" />
                <Button onClick={joinSchoolFn} className="bg-[var(--brand-primary)] hover:opacity-90 text-[var(--brand-primary-fg)] rounded-sm font-medium" data-testid="join-school-btn">
                  <KeyRound className="w-4 h-4 mr-1.5" /> Request
                </Button>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Demo code: <span className="font-mono font-semibold text-[var(--brand-primary)]">BUZZTOWER</span> — Buzz the Tower Aviation Academy
              </div>
            </div>
            {user.role === "super_admin" && (
              <div className="border-t border-slate-200 pt-5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--brand-primary)] mb-2">Create new school (super admin)</div>
                <div className="flex gap-2">
                  <Input value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="Aviation school name" className="bg-white border-slate-300 rounded-sm" data-testid="school-name-input" />
                  <Button onClick={createSchool} variant="outline" className="border-slate-300 hover:border-[var(--brand-primary)] rounded-sm font-medium" data-testid="create-school-btn">Create</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 min-w-[110px]" data-testid={`telemetry-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-slate-500">
        <span className="text-[11px] uppercase tracking-[0.12em] font-semibold">{label}</span>
        <span className="text-[var(--brand-primary)]">{icon}</span>
      </div>
      <div className="font-heading font-bold text-2xl text-slate-900 tabular-nums mt-1">{value}</div>
    </div>
  );
}
